/*
 * App Layout
 * @module features/App
 * @flow
 */

import React, { Fragment, cloneElement } from 'react';
import styled, { css, createGlobalStyle } from 'styled-components/macro';
import { ifProp, ifNotProp } from 'styled-tools';
import { FormattedMessage } from 'react-intl';
import { type ContextRouter } from 'react-router-dom';
import * as R from 'ramda';

import { BottomBar } from '~/components';
import { BottomBarItem } from '~/components/BottomBar/components';
import { BottomBarMenu } from '~/components/BottomBar/components';
import { BottomBarMenuItem } from '~/components/BottomBar/components/BottomBarMenu/components';
import { Modal } from '~/components';

import { Sidebar } from '~/components';
import { type SidebarProps } from '~/components/Sidebar/Sidebar';
import { SidebarItem } from '~/components/Sidebar/components';
import { SidebarLink } from '~/components/Sidebar/components';

import IconJemjar from '@/icons/ic-jemjar.png';
import IconWenda from '@/icons/ic-wenda.png';
import IconMegopoly from '@/icons/ic-megopoly.png';
import IconGame from '@/icons/ic-game.png';
import IconDP from '@/icons/ic-deal-plus.png';

// $FlowIssue
import { ReactComponent as IconArchive } from '@/icons/ic-archive-white.svg';
// $FlowIssue
import { ReactComponent as IconGRC } from '@/icons/ic-grc.svg';
// $FlowIssue
import { ReactComponent as IconInformation } from '@/icons/ic-information.svg';
// $FlowIssue
import { ReactComponent as IconLogout } from '@/icons/ic-logout.svg';
// $FlowIssue
import { ReactComponent as IconNetwork } from '@/icons/ic-network.svg';
// $FlowIssue
import { ReactComponent as IconProfile } from '@/icons/ic-profile.svg';
// $FlowIssue
import { ReactComponent as IconMore } from '@/icons/ic-more-vertical.svg';
// $FlowIssue
import { ReactComponent as IconAndroid } from '@/icons/ic-android-white.svg';
// $FlowIssue
import { ReactComponent as IconWallet } from '@/icons/ic-wallet.svg';
// $FlowIssue
import { ReactComponent as IconAndroidBlue } from '@/icons/ic-android-blue.svg';
// $FlowIssue
import { ReactComponent as IconChat } from '@/icons/ic-chat.svg';

import messages from './messages';
import { isReactNativeAppWebView } from '~/helpers';

export type LayoutProps = ContextRouter &
  SettingsQuery &
  DeviceQuery & {
    children: React$Element<*>,
    intl: $npm$ReactIntl$IntlShape,
    isBottomBarVisible: boolean,
    isModalVisible: boolean,
    location: Location,
    redirectToGRC: () => void,
    signOut: () => void,
    toggleModal: () => void,
  };

const GlobalStyle = createGlobalStyle`
  ${ifProp(
    ['isMobileLandscape'],
    css`
      html {
        transform: rotate(-90deg);
        transform-origin: left top;
        width: 100vh;
        height: 100vw !important;
        overflow-x: hidden;
        position: absolute;
        top: 100%;
        left: 0;
      }
    `,
  )}
`;

const LayoutWrapper = styled.div`
  display: flex;
  height: 100%;

  ${ifProp(
    { device: 'desktop' },
    css`
      min-width: 1024px;
      min-height: 640px;
    `,
    css`
      flex-direction: column;
    `,
  )};
`;

const LayoutMain = styled.div`
  display: flex;
  flex: 1;

  ${ifNotProp(
    { device: 'desktop' },
    css`
      ${ifProp(
        'isBottomBarVisible',
        css`
          max-height: calc(100% - 60px);
        `,
      )}
    `,
    css`
      max-height: auto;
    `,
  )};

  & > div {
    display: flex;
    flex: 1;
  }
`;

const SidebarLinkIcon = styled(SidebarLink)`
  margin: 0;
`;

const ChatIconContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: flex-end;
`;
const ChatIconWrapper = styled.div`
  height: 60%;
  display: flex;
  flex: 1;
  padding-bottom: 12px;
  align-items: center;
  justify-content: center;
`;

const Version = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  color: #fff;
  padding: 4px;
  font-size: 10px;
  z-index: 1;
  opacity: 0.6;
`;

/**
 * Layout React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - children
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @param {Location} [props.location] - location
 * @param {boolean} [props.isBottomBarVisible] - bottom bar visibility flag
 * @param {boolean} [props.isModalVisible] - modal visibility flag
 * @param {$PropertyType<DeviceQuery, 'orientation'>} [props.orientation] - display orientation
 * @param {Settings} [props.settings] - app settings
 * @param {Function} [props.redirectToGRC] - redirect to GRC handler
 * @param {Function} [props.signOut] - sign out handler
 * @param {Function} [props.toggleModal] - toggle modal visibility handler
 * @example <caption>Simple usage</caption>
 * <Layout><div /></Layout>
 * @returns {React.Node<*>} React-component
 */
export const Layout = ({
  children,
  device,
  location,
  intl: { locale },
  isBottomBarVisible,
  isModalVisible,
  orientation,
  settings,
  redirectToGRC,
  redirectToArchivedTransactions,
  signOut,
  toggleModal,
  viewer,
}: LayoutProps) => {
  const routes = {
    information: {
      icon: <IconInformation />,
      label: <FormattedMessage {...messages.information} />,
      link: '/information',
    },
    network: {
      icon: <IconNetwork />,
      label: <FormattedMessage {...messages.network} />,
      link: '/network',
    },
    profile: {
      icon: <IconProfile />,
      label: <FormattedMessage {...messages.profile} />,
      link: '/profile',
    },
    wealth: {
      icon: <IconWallet />,
      label: <FormattedMessage {...messages.wealth} />,
      link: '/wealth',
    },
    archivedTransactions: {
      icon: <IconArchive />,
      label: <FormattedMessage {...messages.archivedTransactions} />,
      link: '/archived-transactions',
    },
  };

  const menuItems = [
    {
      icon: IconGame,
      label: <FormattedMessage {...messages.gameLinkTitle} />,
      links: {
        // $FlowIssue
        en: `${process.env.REACT_APP_GAMES_PLATFORM_ENDPOINT}/en`,
        // $FlowIssue
        cn: `${process.env.REACT_APP_GAMES_PLATFORM_ENDPOINT}/zh-cn`,
      },
    },
    {
      icon: IconMegopoly,
      label: <FormattedMessage {...messages.megopolyLinkTitle} />,
      links: {
        // $FlowIssue
        en: `${process.env.REACT_APP_MONOPOLY_PLATFORM_ENDPOINT}/en`,
        // $FlowIssue
        cn: process.env.REACT_APP_MONOPOLY_PLATFORM_ENDPOINT,
      },
    },
    {
      icon: IconWenda,
      label: <FormattedMessage {...messages.wendaLinkTitle} />,
      links: {
        // $FlowIssue
        en: `${process.env.REACT_APP_WENDA_PLATFORM_ENDPOINT}/en`,
        // $FlowIssue
        cn: `${process.env.REACT_APP_WENDA_PLATFORM_ENDPOINT}/zh-cn`,
      },
    },
    {
      icon: IconJemjar,
      label: <FormattedMessage {...messages.jemjarLinkTitle} />,
      links: {
        // $FlowIssue
        en: `${process.env.REACT_APP_JEMJAR_PLATFORM_ENDPOINT}?locale=en`,
      },
    },
    {
      icon: IconDP,
      label: <FormattedMessage {...messages.dealPlusTitle} />,
      links: {
        // $FlowIssue
        cn: `${process.env.REACT_APP_DEAL_PLUS_PLATFORM_ENDPOINT}`,
      },
    },
  ];

  const hasArchivedTransactions = viewer && viewer.archivedVersions.length > 0;
  const isMobile = device !== 'desktop';
  const isMobileLandscape = isMobile && orientation === 'landscape';
  const open = R.propOr(isMobile, 'isNavigationVisible', settings);
  const routesArr = [
    routes.wealth,
    routes.network,
    routes.profile,
    routes.information,
  ];

  if (!isMobile && hasArchivedTransactions) {
    routesArr.push(routes.archivedTransactions);
  }

  return (
    <LayoutWrapper device={device}>
      <Version>{process.env.REACT_APP_VERSION}</Version>
      <GlobalStyle device={device} isMobileLandscape={isMobileLandscape} />
      {isMobile ? (
        isBottomBarVisible && (
          <BottomBar>
            {() => (
              <Fragment>
                {R.pipe(
                  R.values,
                  R.addIndex(R.map)(({ icon, label, link }, i) => (
                    <BottomBarItem key={i} icon={icon} to={link}>
                      {label}
                    </BottomBarItem>
                  )),
                )(routesArr)}
                <BottomBarMenu
                  version={process.env.REACT_APP_VERSION}
                  icon={<IconMore />}
                  locale={locale}
                  items={
                    <Fragment>
                      <BottomBarMenuItem
                        icon={<IconLogout />}
                        onTouch={signOut}
                      >
                        <FormattedMessage {...messages.logout} />
                      </BottomBarMenuItem>
                      {hasArchivedTransactions && (
                        <BottomBarMenuItem
                          icon={<IconArchive />}
                          onTouch={redirectToArchivedTransactions}
                        >
                          <FormattedMessage
                            {...messages.archivedTransactions}
                          />
                        </BottomBarMenuItem>
                      )}
                      {!isReactNativeAppWebView && (
                        <BottomBarMenuItem
                          icon={<IconAndroid />}
                          href={
                            process.env.REACT_APP_DOWNLOAD_MOBILE_APP_ENDPOINT
                          }
                        >
                          <FormattedMessage {...messages.androidApp} />
                        </BottomBarMenuItem>
                      )}
                      <BottomBarMenuItem
                        icon={<IconChat />}
                        href={process.env.REACT_APP_GRC_CHAT_PLATFORM_ENDPOINT}
                        key={process.env.REACT_APP_GRC_CHAT_PLATFORM_ENDPOINT}
                        isNewTab
                      >
                        <FormattedMessage {...messages.chat} />
                      </BottomBarMenuItem>
                      {R.pipe(
                        R.filter(({ links }) => {
                          if (
                            locale === 'en' &&
                            typeof links.en !== 'undefined'
                          ) {
                            return true;
                          }
                          if (
                            locale === 'zh-CN' &&
                            typeof links.cn !== 'undefined'
                          ) {
                            return true;
                          }
                          return false;
                        }),
                        R.map(({ icon, label, handler, links }) => (
                          <BottomBarMenuItem
                            href={locale === 'en' ? links.en : links.cn}
                            icon={<img alt={label} src={icon} />}
                            onTouch={handler}
                            isNewTab
                          >
                            {label}
                          </BottomBarMenuItem>
                        )),
                      )(menuItems)}
                      <BottomBarMenuItem
                        icon={<IconGRC />}
                        href={process.env.REACT_APP_LANDING_ENDPOINT}
                        isNewTab
                      >
                        <FormattedMessage {...messages.grcLinkTitle} />
                      </BottomBarMenuItem>
                    </Fragment>
                  }
                >
                  <FormattedMessage {...messages.settings} />
                </BottomBarMenu>
              </Fragment>
            )}
          </BottomBar>
        )
      ) : (
        <Sidebar open={open} onLogoClick={redirectToGRC}>
          {({ open }: $Shape<SidebarProps>) => (
            <Fragment>
              {R.pipe(
                R.values,
                R.addIndex(R.map)(({ icon, label, link }, i) => (
                  <SidebarItem key={i} icon={icon} open={open} to={link}>
                    {label}
                  </SidebarItem>
                )),
              )(routesArr)}
              <SidebarItem icon={<IconLogout />} open={open} onClick={signOut}>
                <FormattedMessage {...messages.logout} />
              </SidebarItem>
              <SidebarLink
                open={open}
                href={process.env.REACT_APP_DOWNLOAD_MOBILE_APP_ENDPOINT}
                icon={<IconAndroidBlue />}
              >
                <FormattedMessage {...messages.download} />
              </SidebarLink>
              <ChatIconContainer>
                <ChatIconWrapper>
                  <SidebarLinkIcon
                    open={open}
                    href={process.env.REACT_APP_GRC_CHAT_PLATFORM_ENDPOINT}
                    icon={<IconChat />}
                  >
                    <FormattedMessage {...messages.chat} />
                  </SidebarLinkIcon>
                </ChatIconWrapper>
              </ChatIconContainer>
            </Fragment>
          )}
        </Sidebar>
      )}
      <LayoutMain
        device={device}
        isBottomBarVisible={isBottomBarVisible}
        open={open}
        orientation={orientation}
      >
        {cloneElement(children, { location })}
      </LayoutMain>
      {isMobile && (
        <Modal.Portal
          onClose={toggleModal}
          isVisible={isModalVisible}
          fullscreen
        >
          <FormattedMessage {...messages.gamesPlatformOnlyCnMsg} />
        </Modal.Portal>
      )}
    </LayoutWrapper>
  );
};
